import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Stack,
  TextField,
  TextareaAutosize,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import UploadDropzone from '../UploadDropzone'
import DividerTAG from '../DividerTAG'
import CardTAG from '../CardTAG'
import colors from '../../constants/colors'
import uploadConstants from '../UploadDropzone/constants'
import TableTAG from '../TableTAG'
import timeUtils from '../../utils/timeUtils'
import { IAttachmentsCaseDataPart, IFile } from './types'
import constants from './constants'

import { ReactComponent as DocumentIcon } from '../../assets/images/DocumentIcon.svg'
import { ReactComponent as ImageIcon } from '../../assets/images/ImageIcon.svg'
import { ReactComponent as CameraIcon } from '../../assets/images/CameraIconSmall.svg'
import { ReactComponent as AudioIcon } from '../../assets/images/AudioIcon.svg'
import { ReactComponent as PinIcon } from '../../assets/images/PinIcon.svg'
import { ReactComponent as TrashIcon } from '../../assets/images/TrashIcon.svg'
import { ReactComponent as DownIcon } from '../../assets/images/DownIcon.svg'
import { ReactComponent as RestoreIcon } from '../../assets/images/RestoreIcon.svg'
import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import { userRoles } from '../../store/auth/userTypes'
import ShowForUserRoles from '../ShowForUserTypes'
import { uploadInputZones } from '../UploadDropzone/types'
import { CardTAGColors } from '../CardTAG/types'
import AccordionTAG from '../AccordionTAG'
import { useDispatch, useSelector } from 'react-redux'
import reportsSelectors from '../../store/reports/reportsSelectors'
import {
  addAdditionalMaterial,
  deleteMaterial,
  getReportAdditionalMaterial,
  patchMaterial,
} from '../../store/materials/materialSlice'
import { AppDispatch } from '../../store'
import { IPOSTMaterial } from '../../store/materials/types'
import MaterialMetadata from '../MaterialMetadata'
import FormLayout from '../forms/FormLayout'
import utils from '../CaseInvestigation/utils'
import authSelectors from '../../store/auth/authSelectors'
import { useForm } from 'react-hook-form'
import LabelErrorTAG from '../LabelErrorTAG'
import { textAreaStyle } from '../../constants/styleConstants'

interface FileType {
  [key: string]: string[]
}

const FILE_TYPES: FileType = {
  AUDIO: ['.mp3', '.wav', '.ogg', 'audio/mp3', 'audio/wav', 'audio/ogg'],
  IMAGE: ['.jpg', '.png', '.gif', '.jpeg'],
  DOCUMENT: ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.txt', '.csv', '.odt'],
  VIDEO: ['.mov', '.mp4', '.webm', '.wmv', 'video/mp4', 'video/webm', 'video/wmv', 'video/mov'],
}

export function resolveFileType(extension: string): string | null {
  for (const [type, extensions] of Object.entries(FILE_TYPES)) {
    if (extensions.includes(extension.toLowerCase())) {
      return type
    }
  }
  return null
}

const AttachmentsCaseDataPart: React.FC<IAttachmentsCaseDataPart> = ({
  viewType,
  reportAdditionalMaterials = [],
}) => {
  const [files, setFiles] = useState<{ [key: string]: IFile[] }>({})
  const [selectedItem, setSelectedItem] = useState('')
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([])
  const [pinnedFiles, setPinnedFiles] = useState<IFile[]>([])
  const [trashFiles, setTrashFiles] = useState<IFile[]>([])
  const [selectedFile, setSelectedFile] = useState<IFile>()
  const [unformatedFiles, setUnformatedFiles] = useState(reportAdditionalMaterials)
  const selectedReport = useSelector(reportsSelectors.selectedReport)
  const userEditAccess = useSelector(authSelectors.userReportEditAccess)
  const form = useForm<{ title: string; description: string }>({
    mode: 'onChange',
    defaultValues: {
      title: '',
      description: '',
    },
  })
  const {
    register,
    handleSubmit,
    getValues,
    reset,
    formState: { errors },
  } = form
  const dispatch = useDispatch<AppDispatch>()
  useEffect(() => {
    if (selectedReport?.id)
      dispatch(getReportAdditionalMaterial({ reportId: selectedReport.id as unknown as number }))
  }, [selectedReport])

  useEffect(() => {
    if (reportAdditionalMaterials) {
      setUnformatedFiles(reportAdditionalMaterials)
    }
  }, [reportAdditionalMaterials])

  useEffect(() => {
    if (unformatedFiles) {
      const filteredMaterials = unformatedFiles.filter(
        (file: { is_deleted: boolean }) => file?.is_deleted === false,
      )
      const testObject = {
        images: [
          ...filteredMaterials.filter(
            material => resolveFileType(material.extension) === uploadInputZones.IMAGE,
          ),
        ],
        documents: [
          ...filteredMaterials.filter(
            material => resolveFileType(material.extension) === uploadInputZones.DOCUMENT,
          ),
        ],

        videos: [
          ...filteredMaterials.filter(
            material => resolveFileType(material.extension) === uploadInputZones.VIDEO,
          ),
        ],
        audio: [
          ...filteredMaterials.filter(
            material => resolveFileType(material.extension) === uploadInputZones.AUDIO,
          ),
        ],
      }

      setFiles(testObject)
    }

    setTrashFiles(unformatedFiles?.filter(material => material?.is_deleted === true))

    setPinnedFiles(
      unformatedFiles?.filter(material => material?.is_pinned === true && !material?.is_deleted),
    )
  }, [unformatedFiles])

  const setIsPinned = (id: number, file: IFile) => {
    dispatch(patchMaterial({ fileId: id, material: { is_pinned: !file.is_pinned } })).then(res => {
      const updatedFile = res?.payload as { data: IFile; fileId: number }
      const updatedFiles = reportAdditionalMaterials?.map(file => {
        if (file?.id === updatedFile?.fileId) {
          return updatedFile?.data
        }
        return file
      })
      setUnformatedFiles(updatedFiles)
    })
  }

  const setIsTrash = (id: number, file: IFile) => {
    dispatch(patchMaterial({ fileId: id, material: { is_deleted: !file.is_deleted } })).then(
      res => {
        const updatedFile = res?.payload as { data: IFile; fileId: number }
        const updatedFiles = reportAdditionalMaterials?.map(file => {
          if (file?.id === updatedFile?.fileId) {
            return updatedFile?.data
          }
          return file
        })
        setUnformatedFiles(updatedFiles)
      },
    )
  }

  const handleSelectChange = (
    event: React.MouseEvent<HTMLElement>,
    newSelectedItem: string | null,
  ) => {
    if (newSelectedItem !== null) {
      setSelectedItem(newSelectedItem)
    }
  }

  const addMaterial = (data: any) => {
    const formatedMaterial = {
      title: data.title,
      description: data.description,
      type: selectedItem,
      attachments: [...uploadedFiles],
    } as IPOSTMaterial

    if (formatedMaterial?.attachments?.length) {
      dispatch(addAdditionalMaterial({ data: [formatedMaterial] }))
    }
    reset()
    setSelectedItem('')
    setUploadedFiles([])
  }

  const removeSelectedFile = (fileName: string) => {
    setUploadedFiles(prev => prev.filter((file: File) => file?.name !== fileName))
  }

  const renderAdditionalMaterials = () => {
    const title = getValues('title')
    const description = getValues('description')
    const formatedMaterial = {
      title: title,
      description: description,
      type: selectedItem as uploadInputZones,
      attachments: [
        ...uploadedFiles.map((file: File) => {
          return {
            link: URL.createObjectURL(file),
            name: file?.name,
            size: file?.size,
            type: file?.type,
          }
        }),
      ],
    }

    if (uploadedFiles?.length !== 0) {
      return utils.renderUploadedFiles([formatedMaterial], removeSelectedFile, false)
    }

    return <></>
  }

  const resolveRenderBasedOnSelectedButton = () => {
    if (selectedItem === '') {
      return <></>
    }

    return (
      <>
        <FormLayout>
          <DividerTAG />
          <UploadDropzone
            onChange={(file: any) => {
              setUploadedFiles(prev => [...prev, ...file])
            }}
            iconAndText={uploadConstants.getDropzoneIconsAndText({
              type: selectedItem,
              themeColor: colors.TAG_BLUE,
            })}
            acceptType={uploadConstants.dropzoneAcceptanceType[selectedItem]}
            mainColor={colors.TAG_BLUE}
          />
          <form onSubmit={handleSubmit(addMaterial)}>
            <div className='t-field t-vbox'>
              <LabelErrorTAG label={'Title'} mandatory />
              <TextField
                variant='standard'
                type={'text'}
                {...register('title', {
                  required: { value: true, message: 'Title is required' },
                })}
                fullWidth
              />
              <span className='t-field-error-msg t-color-red t-font-size-xxs'>
                {errors?.title?.message}
              </span>
            </div>
            <div className='t-field t-vbox'>
              <LabelErrorTAG label={'Description'} />
              <TextareaAutosize {...register('description')} minRows={5} style={textAreaStyle} />
            </div>
            {uploadedFiles.length !== 0 && (
              <div className='t-vbox t-upload-table'>{renderAdditionalMaterials()}</div>
            )}
            <Button
              variant='outlined'
              startIcon={<AddIcon />}
              sx={{ color: colors.BLACK }}
              type='submit'
              disabled={!!errors?.title?.message || !uploadedFiles.length}
            >
              <Typography variant='bodyRegular' color='inherit'>
                Add Material
              </Typography>
            </Button>
          </form>
        </FormLayout>
      </>
    )
  }

  const handleSelect = (data: IFile) => {
    setSelectedFile(data)
  }

  const downloadFile = ({ fileUrl, fileName }: { fileUrl: string; fileName: string }) => {
    const link = document.createElement('a')
    link.href = fileUrl
    link.download = `${fileName}`
    link.click()
  }

  const pernamentlyDeleteFile = (id: number) => {
    dispatch(deleteMaterial({ fileId: id }))
  }

  const renderListFiles = (data: IFile[]) => {
    return (
      <TableTAG
        columns={constants.testColumns(setIsPinned, setIsTrash, downloadFile, !userEditAccess)}
        pagination={false}
        rows={data}
        onRowClick={handleSelect}
        colWidths={['10%', '10%', '20%', '20%', '20%']}
      />
    )
  }

  const getIconColors = (isPinnedFile: boolean) => {
    if (!userEditAccess) {
      return colors.TAG_TEXT_GRAY
    }
    return isPinnedFile ? colors.TAG_RED : colors.BLACK
  }

  const renderTileFiles = (
    data: IFile[],
    boxError = false,
    color: CardTAGColors = 'tagBoxSuccess',
  ) => {
    return data.map(file => {
      const pinColor = getIconColors(file?.is_pinned)
      return (
        <Box
          onClick={() => {
            handleSelect(file)
          }}
          key={file.uuid}
        >
          <CardTAG
            title={
              <Box
                sx={{
                  width: '80%',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                }}
              >
                <Typography variant='h4' sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {file?.name}
                </Typography>
              </Box>
            }
            boxError={boxError}
            color={color}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                gap: '1.8rem',
                flex: 1,
                width: '100%',
              }}
            >
              <Box sx={{ width: '100%', height: '30vh' }}>{constants?.resolveFileIcon(file)}</Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                {/* <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant='labelBold'>Uploaded By</Typography>
                  <Typography variant='textMedium'>
                    {file?.uploader?.first_name} {file?.uploader?.last_name}
                  </Typography>
                </Box> */}
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant='labelBold'>Upload Date</Typography>
                  <Typography variant='textMedium'>
                    {timeUtils.getFormatedDate(file?.uploaded_at)}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: '2rem' }}>
                <Button
                  variant='outlined'
                  startIcon={<DownIcon fill={colors.BLACK} />}
                  sx={{ flex: 1 }}
                  onClick={e => {
                    e.stopPropagation()
                    downloadFile({
                      fileUrl: file.link,
                      fileName: file.name,
                    })
                  }}
                >
                  <Typography variant='bodyRegular' color='inherit'>
                    Download
                  </Typography>
                </Button>
                {file.is_deleted ? (
                  <Button
                    variant='outlined'
                    startIcon={<RestoreIcon fill={pinColor} />}
                    sx={{ flex: 1 }}
                    onClick={e => {
                      e.stopPropagation()
                      setIsTrash(file.id, file)
                    }}
                    disabled={!userEditAccess}
                  >
                    <Typography variant='bodyRegular' color='inherit'>
                      Restore
                    </Typography>
                  </Button>
                ) : (
                  <Button
                    variant='outlined'
                    startIcon={<PinIcon fill={pinColor} />}
                    sx={{ flex: 1, color: pinColor, borderColor: `${pinColor}!important` }}
                    onClick={e => {
                      e.stopPropagation()
                      setIsPinned(file.id, file)
                    }}
                    disabled={!userEditAccess}
                  >
                    <Typography variant='bodyRegular' color='inherit'>
                      {file.is_pinned ? 'Unpin' : 'Pin'}
                    </Typography>
                  </Button>
                )}
              </Box>
              <Button
                variant='outlined'
                startIcon={file.is_deleted ? <CloseIcon /> : <TrashIcon fill={pinColor} />}
                onClick={e => {
                  e.stopPropagation()
                  if (file.is_deleted) {
                    pernamentlyDeleteFile(file.id)
                    return
                  }
                  setIsTrash(file.id, file)
                }}
                sx={{ width: '100%' }}
                disabled={!userEditAccess}
              >
                <Typography variant='bodyRegular' color='inherit'>
                  {file.is_deleted ? 'Permanently Delete' : 'Delete'}
                </Typography>
              </Button>
            </Box>
          </CardTAG>
        </Box>
      )
    })
  }

  const renderTilesItems = (
    title: string | React.ReactNode,
    files: IFile[],
    boxError = false,
    color: CardTAGColors = 'tagBoxSuccess',
  ) => {
    return (
      <AccordionTAG boxError={boxError} title={title}>
        {files.length > 0 && renderTileFiles(files, boxError, color)}
      </AccordionTAG>
    )
  }
  const renderListItems = (
    title: string | React.ReactNode,
    files: IFile[],
    boxError = false,
    color: CardTAGColors = 'tagBoxSuccess',
  ) => {
    return (
      <CardTAG title={title} boxError={boxError} color={color}>
        {files.length > 0 && renderListFiles(files)}
      </CardTAG>
    )
  }

  const resolveRender = (
    title: string | React.ReactNode,
    files: IFile[] = [],
    boxError = false,
    color: CardTAGColors = 'tagBoxSuccess',
  ) => {
    return viewType === 'box'
      ? renderTilesItems(title, files, boxError, color)
      : renderListItems(title, files, boxError, color)
  }

  return (
    <>
      <Stack spacing={3}>
        {userEditAccess && (
          <CardTAG title='Upload New Attachments'>
            <Box sx={{ p: '2rem', width: '100%' }}>
              <ToggleButtonGroup
                exclusive
                value={selectedItem}
                onChange={handleSelectChange}
                sx={[constants.buttonsGroupStyle]}
              >
                <ToggleButton
                  value={uploadInputZones.IMAGE}
                  sx={[constants.buttonsGroupButton, constants.buttonBorderRight]}
                >
                  <ImageIcon fill={colors.TAG_BLUE} />
                  <Typography variant='bodyBold' sx={{ color: colors.TAG_BLUE }}>
                    Image
                  </Typography>
                </ToggleButton>
                <ToggleButton
                  value={uploadInputZones.DOCUMENT}
                  sx={[constants.buttonsGroupButton, constants.buttonBorderRight]}
                >
                  <DocumentIcon fill={colors.TAG_BLUE} />
                  <Typography variant='bodyBold' sx={{ color: colors.TAG_BLUE }}>
                    Document
                  </Typography>
                </ToggleButton>
                <ToggleButton
                  value={uploadInputZones.VIDEO}
                  sx={[constants.buttonsGroupButton, constants.buttonBorderRight]}
                >
                  <CameraIcon fill={colors.TAG_BLUE} />
                  <Typography variant='bodyBold' sx={{ color: colors.TAG_BLUE }}>
                    Video
                  </Typography>
                </ToggleButton>
                <ToggleButton value={uploadInputZones.AUDIO} sx={[constants.buttonsGroupButton]}>
                  <AudioIcon fill={colors.TAG_BLUE} />
                  <Typography variant='bodyBold' sx={{ color: colors.TAG_BLUE }}>
                    Audio
                  </Typography>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {resolveRenderBasedOnSelectedButton()}
          </CardTAG>
        )}
        {resolveRender(
          <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <PinIcon fill={colors.TAG_RED} />
            <Typography variant={`${viewType === 'box' ? 'h1' : 'h3'}`}>Pinned</Typography>
          </Box>,
          pinnedFiles,
          true,
        )}
        {resolveRender('Images', files['images'])}
        {resolveRender('Documents', files['documents'])}
        {resolveRender('Video', files['videos'])}
        {resolveRender('Audio', files['audio'])}

        <ShowForUserRoles roles={[userRoles.ADMIN]}>
          {resolveRender(
            <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <TrashIcon fill={colors.BLACK} stroke={colors.BLACK} />
              <Typography variant='bodyBold' sx={{ color: colors.BLACK }}>
                Trash
              </Typography>
            </Box>,
            trashFiles,
            false,
            'tagBoxArchive',
          )}
        </ShowForUserRoles>
      </Stack>
      <MaterialMetadata
        opened={!!selectedFile}
        close={() => {
          setSelectedFile(undefined)
        }}
        material={selectedFile}
      />
    </>
  )
}

export default AttachmentsCaseDataPart
